import { PwycPriceManager } from "@shared/modules/seating/services/PwycPriceManager";
import { common, enums } from "@shared/constants";
import { useBasketService } from "@shared/modules/basket/context/BasketServiceContext";
import { showTicketButtons } from "@shared/modules/events/utils/detail-page-helpers";
import { useSeatingState } from "@shared/modules/seating/contexts/useSeatingState";
import { isOnSaleTicketType } from "@shared/modules/ticket-types/utils/ticket-type-utils";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { DefaultLocationSelectProps } from "./DefaultLocationSelect.props";

const pwycPriceManager = PwycPriceManager.getInstance();

export function useDefaultLocationSelect(props: DefaultLocationSelectProps) {
  const {
    item,
    itemsOnSeat,
    voucherData,
    ticketAuthArray,
    updateTicketAuthArray,
    selectItemPermission,
  } = props;

  const { seatingServiceState, seatingEventData } = useSeatingState();
  const { mapItemType } = seatingServiceState;
  const itemType =
    mapItemType === common.SeatingMapItemType.MEMBERSHIP
      ? enums.ITEM_TYPES_MAP.ITEM_TYPE_MEMBERSHIP.id
      : enums.ITEM_TYPES_MAP.ITEM_TYPE_TICKET.id;
  const [pwycPrice, setPwycPrice] = useState<string | null>(
    pwycPriceManager.getPwycPrice(item.id, itemType) || null
  );
  const pwycPriceRef = useRef<string | null | undefined>(pwycPrice);
  const purchaseLimits = seatingServiceState.overridePurchaseLimits;

  const {
    isBasketUpdating,
    purchaseSourcePlatform,
    paymentType,
    itemGroups,
    areAllRenewalItemGroups,
  } = useBasketService();

  const isGroupSale = useMemo(
    () => seatingServiceState.isGroupSalesMap(),
    [seatingServiceState]
  );
  const isDynamicHold = useMemo(
    () => Boolean(seatingServiceState.basketHoldID),
    [seatingServiceState]
  );

  // allow sold out items to be selected on group sales maps
  const isSoldOut = useMemo(
    () =>
      (item?.usageRemaining === 0 || item?.sold_out) &&
      !seatingServiceState.isGroupSalesMap() &&
      !isDynamicHold,
    [seatingServiceState, item, isDynamicHold]
  );

  const showButtons =
    (showTicketButtons(
      item,
      seatingEventData,
      voucherData.userVouchers,
      ticketAuthArray,
      updateTicketAuthArray,
      voucherData.basketVoucherCode,
      isDynamicHold
    ) ||
      isGroupSale ||
      isDynamicHold ||
      mapItemType === common.SeatingMapItemType.MEMBERSHIP) &&
    !isSoldOut;

  const getMinPurchaseLimit = useCallback(() => {
    if (purchaseLimits) {
      return purchaseLimits.min;
    }
    if (item.minimum_purchase_limit) {
      return item.minimum_purchase_limit;
    }
    // 1 is the default minimum purchase limit for items
    return item.$countOptions?.[1] ?? common.DEFAULT_MIN_TICKET_PURCHASE_LIMIT;
  }, [purchaseLimits, item]);

  const getMaxPurchaseLimit = useCallback(() => {
    const ticketPermissions = itemsOnSeat?.$permissions;

    if (purchaseLimits) {
      return purchaseLimits.max;
    }
    if (isGroupSale) {
      return ticketPermissions?.[0].usage_remaining ?? 1;
    }
    // 8 is the default maximum purchase limit for items
    return (
      item.$countOptions?.[item.$countOptions?.length - 1] ??
      common.DEFAULT_MAX_TICKET_PURCHASE_LIMIT
    );
  }, [itemsOnSeat, purchaseLimits, isGroupSale, item]);

  const isDisabledSelector = useMemo(
    () =>
      isBasketUpdating ||
      (!isOnSaleTicketType(item) &&
        mapItemType !== common.SeatingMapItemType.MEMBERSHIP),
    [isBasketUpdating, item, mapItemType]
  );

  const selectItemsCallback = (val: number) => {
    selectItemPermission({
      itemTypeId: item.id,
      permissionId: item.permissionId,
      requestAmount: val,
      minPurchaseLimit: getMinPurchaseLimit(),
      pwycPrice: pwycPriceRef.current,
    });
  };

  const setPwycPriceCallback = (value: string) => {
    pwycPriceManager.setPwycPrice(item.id, value, itemType);

    selectItemPermission({
      itemTypeId: item.id,
      permissionId: item.permissionId,
      minPurchaseLimit: getMinPurchaseLimit(),
      requestAmount: item.requestAmount,
      pwycPrice: value,
    });

    return setPwycPrice(value);
  };

  const pwycConfig = item?.pay_what_you_can_configuration;

  const currentPwycPrice = itemGroups?.find(
    (ig) => ig.type === item.id && Boolean(ig.pay_what_you_can_price)
  )?.pay_what_you_can_price;

  /** Keep the pwycPrice updated when state changes */
  useEffect(() => {
    pwycPriceRef.current = pwycPrice;
  }, [pwycPrice]);

  return {
    selectItemsCallback,
    getMinPurchaseLimit,
    getMaxPurchaseLimit,
    isDisabledSelector,
    showButtons,
    isSoldOut,
    seatingEventData,
    purchaseSourcePlatform,
    paymentType,
    purchaseLimits,
    pwycConfig,
    currentPwycPrice,
    areAllRenewalItemGroups,
    pwycPrice,
    setPwycPriceCallback,
  };
}
